import type { ChatSettings } from '../hooks/useChat'
import type { Mode } from '../lib/types'
import { CATEGORY_PRESETS } from '../lib/categoryPresets'
import { useI18n } from '../lib/i18n'

interface Props {
  settings: ChatSettings
  onChange: (s: ChatSettings) => void
  disabled?: boolean
}

export function SettingsPanel({ settings, onChange, disabled }: Props) {
  const { s } = useI18n()
  const set = <K extends keyof ChatSettings>(k: K, v: ChatSettings[K]) =>
    onChange({ ...settings, [k]: v })

  const togglePreset = (key: string) => {
    const next = settings.categoryPresets.includes(key)
      ? settings.categoryPresets.filter((k) => k !== key)
      : [...settings.categoryPresets, key]
    set('categoryPresets', next)
  }
  const clearPresets = () => set('categoryPresets', [])
  const allKeys = CATEGORY_PRESETS.map((p) => p.key)

  return (
    <div className="space-y-4 text-sm">
      <div>
        <label className="block text-[11px] font-medium text-brand-500 mb-1 uppercase tracking-wide">
          {s.retrievalMode}
        </label>
        <select
          value={settings.mode}
          onChange={(e) => set('mode', e.target.value as Mode)}
          disabled={disabled}
          className="w-full rounded border border-brand-200 px-2 py-1.5 text-sm bg-white disabled:bg-brand-100 disabled:text-brand-400"
        >
          <option value="hybrid">{s.modeHybrid}</option>
          <option value="dense">{s.modeDense}</option>
          <option value="bm25">{s.modeBm25}</option>
        </select>
      </div>

      <div>
        <div className="flex items-baseline justify-between text-[11px] font-medium text-brand-500 mb-1 uppercase tracking-wide">
          <span>{s.topK}</span>
          <span className="font-mono text-brand-700 text-xs">
            {settings.top_k}
          </span>
        </div>
        <input
          type="range"
          min={1}
          max={30}
          step={1}
          value={settings.top_k}
          onChange={(e) => set('top_k', Number(e.target.value))}
          disabled={disabled}
          className="w-full accent-brand-700"
        />
      </div>

      <div>
        <div className="flex items-baseline justify-between text-[11px] font-medium text-brand-500 mb-1 uppercase tracking-wide">
          <span>{s.temperature}</span>
          <span className="font-mono text-brand-700 text-xs">
            {settings.temperature.toFixed(2)}
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={2}
          step={0.05}
          value={settings.temperature}
          onChange={(e) => set('temperature', Number(e.target.value))}
          disabled={disabled}
          className="w-full accent-brand-700"
        />
      </div>

      <label className="flex items-start gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={settings.phrase}
          onChange={(e) => set('phrase', e.target.checked)}
          disabled={disabled}
          className="mt-0.5 accent-brand-700"
        />
        <div>
          <div className="text-xs text-brand-700 font-medium">
            {s.phraseMode}
          </div>
          <div className="text-[11px] text-brand-400 leading-snug">
            {s.phraseDescBefore}
            <code className="font-mono">phraseto_tsquery</code>
            {s.phraseDescAfter}
          </div>
        </div>
      </label>

      <div className="pt-3 border-t border-brand-100">
        <div className="flex items-baseline justify-between text-[11px] font-medium text-brand-500 mb-1 uppercase tracking-wide">
          <span>{s.categoryFilter}</span>
          <button
            type="button"
            onClick={clearPresets}
            disabled={disabled || settings.categoryPresets.length === 0}
            className="text-[10px] font-normal normal-case text-brand-400 hover:text-brand-700 disabled:opacity-40 disabled:hover:text-brand-400"
          >
            {s.categoryFilterAll}
          </button>
        </div>
        <p className="text-[11px] text-brand-400 leading-snug mb-2">
          {s.categoryFilterDesc}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {allKeys.map((key) => {
            const active = settings.categoryPresets.includes(key)
            const label = s.categoryPreset[key] ?? key
            return (
              <button
                type="button"
                key={key}
                onClick={() => togglePreset(key)}
                disabled={disabled}
                aria-pressed={active}
                className={
                  'rounded-full px-2.5 py-0.5 text-[11px] border transition-colors '
                  + (active
                    ? 'bg-brand-700 text-white border-brand-700 hover:bg-brand-800'
                    : 'bg-white text-brand-600 border-brand-200 hover:border-brand-400 hover:text-brand-800')
                  + ' disabled:opacity-50 disabled:cursor-not-allowed'
                }
              >
                {label}
              </button>
            )
          })}
        </div>
        <div className="mt-1.5 text-[10px] text-brand-400 font-mono">
          {s.categoryFilterSummary(settings.categoryPresets.length, allKeys.length)}
        </div>
      </div>
    </div>
  )
}
